import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, CreditCard, CalendarDays, Loader2, User, FileText } from "lucide-react";
import { toast } from "sonner";

export default function User360() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);

  useEffect(() => {
    api.get(`/admin/users/${userId}/full`)
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load User 360"))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!data) return null;

  const paymentRows = [
    ...(data.payments?.payment_transactions || []).map((item) => ({ ...item, transaction_type: "payment" })),
    ...(data.payments?.eplayment_transactions || []).map((item) => ({ ...item, transaction_type: "eplayment" })),
    ...(data.payments?.wallet_transactions || []).map((item) => ({ ...item, transaction_type: "wallet" })),
    ...(data.payments?.refunds || []).map((item) => ({ ...item, transaction_type: "refund" })),
  ].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6" data-testid="user-360-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground"><ArrowLeft className="w-5 h-5" /></button>
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">User 360</h1>
          <p className="text-sm text-muted-foreground">{data.user.name} · {data.user.email}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Metric title="Total Bookings" value={data.stats.total_booking_count} icon={CalendarDays} />
        <Metric title="Payments" value={`₱${Number(data.stats.total_payments || 0).toLocaleString()}`} icon={CreditCard} />
        <Metric title="Refunds" value={`₱${Number(data.stats.total_refunds || 0).toLocaleString()}`} icon={CreditCard} />
        <Metric title="Classes / Sessions" value={data.stats.total_classes_sessions} icon={CalendarDays} />
        <Metric title="Personas" value={(data.user.personas || []).length} icon={User} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Registration & Documents</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <p><span className="text-muted-foreground">Name:</span> {data.user.name}</p>
              <p><span className="text-muted-foreground">Email:</span> {data.user.email}</p>
              <p><span className="text-muted-foreground">Phone:</span> {data.user.phone || '—'}</p>
              <p><span className="text-muted-foreground">Roles:</span> {(data.user.personas || []).join(', ') || '—'}</p>
              <p><span className="text-muted-foreground">KYC Status:</span> {data.user.kyc_status || '—'}</p>
              <p><span className="text-muted-foreground">Verified:</span> {String(data.user.identity_verified)}</p>
            </div>
            {(data.player_kyc?.gov_id_url || data.player_kyc?.selfie_url) && (
              <div className="space-y-2">
                {data.player_kyc.gov_id_url && <a href={data.player_kyc.gov_id_url} target="_blank" rel="noreferrer" className="text-primary hover:underline">View Government ID</a>}
                {data.player_kyc.selfie_url && <a href={data.player_kyc.selfie_url} target="_blank" rel="noreferrer" className="text-primary hover:underline block">View Selfie</a>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Metrics</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total Bookings</span><span>{data.stats.total_booking_count}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Payments</span><span>₱{Number(data.stats.total_payments || 0).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Refunds</span><span>₱{Number(data.stats.total_refunds || 0).toLocaleString()}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total Classes / Sessions</span><span>{data.stats.total_classes_sessions}</span></div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Activity — Bookings</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data.recent_bookings || []).map((booking) => (
            <div key={booking.booking_id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`user-360-booking-${booking.booking_id}`}>
              <div>
                <p className="font-medium">{booking.venue_name || booking.resource_name || booking.booking_id}</p>
                <p className="text-xs text-muted-foreground">{booking.created_at?.slice(0, 16).replace('T', ' ')} · {booking.status}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelectedBooking(booking)}>View Details</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Activity — Classes / Sessions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(data.sessions_attended || []).map((item) => (
            <div key={item.participant_id} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`user-360-session-${item.participant_id}`}>
              <div>
                <p className="font-medium">{item.session_id}</p>
                <p className="text-xs text-muted-foreground">{item.joined_at?.slice(0, 16).replace('T', ' ')} · {item.status} · {item.payment_status}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelectedBooking(item)}>View Details</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-heading text-lg">Transactions</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {paymentRows.map((payment, idx) => (
            <div key={`${payment.transaction_type}-${payment.transaction_id || payment.reference_id || idx}`} className="rounded-lg border border-slate-200 p-3 flex items-center justify-between gap-3" data-testid={`user-360-payment-${idx}`}>
              <div>
                <p className="font-medium capitalize">{payment.transaction_type}</p>
                <p className="text-xs text-muted-foreground">{payment.booking_id || payment.reference_id || payment.transaction_id || 'Reference unavailable'}</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => setSelectedPayment(payment)}>View Details</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={!!selectedBooking} onOpenChange={() => setSelectedBooking(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="user-360-booking-dialog">
          <DialogHeader><DialogTitle>Booking / Session Details</DialogTitle></DialogHeader>
          {selectedBooking && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(selectedBooking, null, 2)}</pre>}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedPayment} onOpenChange={() => setSelectedPayment(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="user-360-payment-dialog">
          <DialogHeader><DialogTitle>Payment / Refund Details</DialogTitle></DialogHeader>
          {selectedPayment && <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(selectedPayment, null, 2)}</pre>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ title, value, icon: Icon }) {
  return (
    <Card>
      <CardContent className="p-4">
        <Icon className="w-5 h-5 text-court-orange mb-2" />
        <p className="font-heading text-2xl font-bold">{value}</p>
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{title}</p>
      </CardContent>
    </Card>
  );
}